import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { getKDWBaseUrl } from '../helpers/kdw-base-url.helper';
import { IDMDealSegmentCatalog, IDMDealsSegmentCatalog, IKMLineItemSegmentCatalog } from '../interfaces';
import { DMDealsSegmentCatalog, KMLineItemsSegmentCatalog } from '../models';
import { OktaHttpReqService } from './okta-http-req.service';


@Injectable()
export class CtxSegmentService {
  private endpoint = 'api/v1/contextual/segments';

  constructor (private oktaHttpReqService: OktaHttpReqService) {}

  private get kdwEndpoint(): string {
    return `${getKDWBaseUrl()}/api/v1/contextual/segments`;
  }

  getAllCtxSegments(params) {
    return this.oktaHttpReqService.request('get', this.kdwEndpoint, {params});
  }

  getCtxSegment(id) {
    return this.oktaHttpReqService.request('get', `${this.kdwEndpoint}/${id}`);
  }

  saveCtxSegment(params) {
    if (params.ID) {
      // Is editing segment
      return this.oktaHttpReqService.request('put', `${this.kdwEndpoint}/${params.ID}`, params);
    } else {
      return this.oktaHttpReqService.request('post', this.kdwEndpoint, params);
    }
  }

  deleteCtxSegment(id) {
    return this.oktaHttpReqService.request('delete', `${this.kdwEndpoint}/${id}`);
  }

  updateTargetingFocus(id: number, similarityThreshold: string): Observable<any> {
    return this.oktaHttpReqService.request('put', `${this.kdwEndpoint}/${id}`, { similarity_threshold: similarityThreshold });
  }

  /**
   * Get DM Deal Segment Catalog
   * @param id Segment ID to retrive DM Deals Segment Catalog
   * @returns an observable containing Deals Segment Catalog
   */
  getCtxDMDealSegmentCatalog(id: number): Observable<DMDealsSegmentCatalog> {
    return this.oktaHttpReqService.request<IDMDealSegmentCatalog[]>('get', `${this.kdwEndpoint}/${id}/dm-deals-segment-catalog`).pipe(
      map((segmentCatalog: IDMDealSegmentCatalog[]) => new DMDealsSegmentCatalog(segmentCatalog)),
    );
  }

  /**
   * Get KM Line Item Segment Catalog
   * @param id Segment ID to retrieve KM Line Items Segment Catalog
   * @returns an observable containing Line Items Segment Catalog
   */
  getKMLineItemSegmentCatalog(id: number): Observable<KMLineItemsSegmentCatalog> {
    return this.oktaHttpReqService.request<IKMLineItemSegmentCatalog[]>('get', `${this.kdwEndpoint}/${id}/km-line-items-segment-catalog`)
      .pipe(map((segmentCatalog: IKMLineItemSegmentCatalog[]) => new KMLineItemsSegmentCatalog(segmentCatalog)),
    );
  }

  saveCustomCtxSegment(params) {
    return this.oktaHttpReqService.request('post', `${this.endpoint}/custom`, params);
  }

  savePromptedCtxSegment(params) {
    return this.oktaHttpReqService.request('post', `${this.kdwEndpoint}/adspresso`, params);
  }
  verifyPromptedCtxSegment(params): Observable<string> {
    return this.oktaHttpReqService.request<string>('post', `${this.kdwEndpoint}/adspresso/verify`, params);
  }

  // External API call to get refined prompt
  getRefinedPrompt(userPrompt: string): Observable<string> {
    const baseUrl = getKDWBaseUrl();
    const endpoint = '/api/v2/contextual/segments/adspresso/refine-prompt';

    return this.oktaHttpReqService.request<string>(
      'post',
      `${baseUrl}${endpoint}`,
      { prompt: userPrompt },
    );
  }

  getSegmentPreview(segmentPrompt: string): Observable<any> {
    const baseUrl = getKDWBaseUrl();
    const endpoint = `/api/v2/contextual/segments/adspresso/preview`;

    return this.oktaHttpReqService.request<any>(
      'post',
      `${baseUrl}${endpoint}`,
      { prompts: [segmentPrompt], limit: 15 },
    );
  }

  getTotalURLCounts(segmentPrompt: string): Observable<any> {
    const baseUrl = getKDWBaseUrl();
    const endpoint = `/api/v2/contextual/segments/adspresso/url-count`;

    return this.oktaHttpReqService.request<any>(
      'post',
      `${baseUrl}${endpoint}`,
      { prompt: segmentPrompt },
    );
  }

  getTotalURLCountsMulti(segmentPrompts: string[]): Observable<any> {
    const baseURL = getKDWBaseUrl();
    const endpoint = `/api/v2/contextual/segments/adspresso/url-count-multi`;

    return this.oktaHttpReqService.request<any>(
      'post',
      `${baseURL}${endpoint}`,
      { prompts: segmentPrompts },
    );
  }

  validateAudienceBrief(type: string, brief: string | File): Observable<any> {
    const baseUrl = getKDWBaseUrl();
    const freeFormEndpoint = `/api/v2/contextual/segments/adspresso/validate-brief`;
    const fileUploadEndpoint = `/api/v2/contextual/segments/adspresso/validate-brief-pdf`;

    let requestBody: FormData | { brief: string };

    if (type === 'freeForm') {
      requestBody = { brief: brief as string };
    } else {
      const formData = new FormData();
      formData.append('file', brief as File);
      requestBody = formData;
    }

    return this.oktaHttpReqService.request<any>(
      'post',
      type === 'freeForm' ? `${baseUrl}${freeFormEndpoint}` : `${baseUrl}${fileUploadEndpoint}`,
      requestBody,
    );
  }

  generateSegmentCards(type: string, brief: string | File): Observable<any> {
    const baseUrl = getKDWBaseUrl();
    const freeFormEndpoint = `/api/v2/contextual/segments/adspresso/generate-segment-cards`;
    const fileUploadEndpoint = `/api/v2/contextual/segments/adspresso/generate-segment-cards-from-pdf`;

    let requestBody: FormData | { brief: string };

    if (type === 'freeForm') {
      requestBody = { brief: brief as string };
    } else {
      const formData = new FormData();
      formData.append('file', brief as File);
      requestBody = formData;
    }

    return this.oktaHttpReqService.request<any>(
      'post',
      type === 'freeForm' ? `${baseUrl}${freeFormEndpoint}` : `${baseUrl}${fileUploadEndpoint}`,
      requestBody,
    );
  }
}
